import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../../models/base';

const LIST_ENDPOINT = '/stripe';
const SUBSCRIPTION_V2_ENDPOINT = '/api/v2/subscription';
const STRIPE_V2_ENDPOINT = '/api/v2/stripe';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  constructor(private http: HttpClient) {}

  // Los endpoints V2 devuelven SingleResponseDto<T> con casing PascalCase (Newtonsoft sin
  // resolver camelCase configurado); se admite también lowercase por si acaso.
  private unwrapValue(response: any): any {
    if (!response) return null;
    return response.Value !== undefined ? response.Value : response.value;
  }

  private mapCheckoutSessionResponse(response: any): string {
    const value = this.unwrapValue(response);
    if (!value) return null;

    const outcome = value.Outcome !== undefined ? value.Outcome : value.outcome;
    const sessionId = value.SessionId !== undefined ? value.SessionId : value.sessionId;

    if (outcome === "redirect") return sessionId;
    if (outcome === "free_ok") return "FREE_OK";
    return "OK"; // outcome === "ok"
  }

  createCheckoutSessionId(data: any): Promise<any> {
    const body = {
      planAlias: data && data.suscription ? data.suscription.id : null,
      period: data ? data.period : null,
      couponCode: data ? data.couponCode : null,
    };
    return firstValueFrom(
      this.http.post<any>(environment.apiUrl + SUBSCRIPTION_V2_ENDPOINT + '/checkout', body)
    ).then((response) => this.mapCheckoutSessionResponse(response));
  }

  changeSubscriptionPlan(data: any): Promise<any> {
    const body = {
      planAlias: data && data.suscription ? data.suscription.id : null,
      period: data ? data.period : null,
      prorationDate: data && data.prorationDate ? data.prorationDate : null,
    };
    return firstValueFrom(
      this.http.post<any>(environment.apiUrl + SUBSCRIPTION_V2_ENDPOINT + '/change-plan', body)
    ).then((response) => this.mapCheckoutSessionResponse(response));
  }

  previewPlanChange(planAlias: string, period: string): Promise<any> {
    return firstValueFrom(
      this.http.get<any>(environment.apiUrl + SUBSCRIPTION_V2_ENDPOINT + '/change-plan/preview', {
        params: { planAlias, period }
      })
    ).then((response) => this.unwrapValue(response));
  }

  createUpdateCardSession(): Promise<any> {
    return firstValueFrom(
      this.http.post<ApiResponse<any>>(environment.apiUrl + LIST_ENDPOINT + '/update-card', {})
    ).then((response) => response.data);
  }

  cancelAccount(): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(environment.apiUrl + SUBSCRIPTION_V2_ENDPOINT + '/cancel', {})
    ).then((response) => this.unwrapValue(response));
  }

  createCheckoutOCRSessionId(data: any): Promise<any> {
    const body = {
      quantity: data ? data.quantity : 0,
    };
    return firstValueFrom(
      this.http.post<any>(environment.apiUrl + STRIPE_V2_ENDPOINT + '/ocr/checkout', body)
    ).then((response) => this.mapCheckoutSessionResponse(response));
  }

  cancelStripeSubscription(id: any): Promise<string> {
    return firstValueFrom(
      this.http.post<any>(environment.apiUrl + SUBSCRIPTION_V2_ENDPOINT + '/cancel', {})
    ).then((response) => this.unwrapValue(response));
  }

  reActivateStripeSubscription(id: any): Promise<string> {
    return firstValueFrom(
      this.http.post<any>(environment.apiUrl + SUBSCRIPTION_V2_ENDPOINT + '/reactivate', {})
    ).then((response) => this.unwrapValue(response));
  }

  validateCoupon(request: any): Promise<any> {
    const body = {
      couponCode: request ? request.couponCode : null,
      planAlias: request ? request.planAlias : null,
      period: request ? request.period : null,
    };
    return firstValueFrom(
      this.http.post<any>(environment.apiUrl + SUBSCRIPTION_V2_ENDPOINT + '/coupon/validate', body)
    ).then((response) => this.mapCouponValidationResponse(response));
  }

  private mapCouponValidationResponse(response: any): any {
    const value = this.unwrapValue(response);
    if (!value) return null;

    const pick = (pascal: string, camel: string) =>
      value[pascal] !== undefined ? value[pascal] : value[camel];

    return {
      valid: pick('Valid', 'valid') === true,
      code: pick('Code', 'code'),
      description: pick('Description', 'description'),
      percentOff: pick('PercentOff', 'percentOff'),
      amountOff: pick('AmountOff', 'amountOff'),
      duration: pick('Duration', 'duration'),
      durationInMonths: pick('DurationInMonths', 'durationInMonths'),
      rejectionReason: pick('RejectionReason', 'rejectionReason'),
      message: pick('Message', 'message'),
    };
  }

  applyCoupon(promoCode: string): Promise<any> {
    return firstValueFrom(
      this.http.post<ApiResponse<any>>(environment.apiUrl + LIST_ENDPOINT + '/apply-coupon', { promoCode: promoCode })
    ).then((response) => response.data);
  }
}